import React, { useCallback } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { useDispatch, useSelector } from 'react-redux';

import {
  createConfirmation,
  closeConfirmationByID,
  getActiveConfirmation,
  getHasActiveConfirmation,
} from 'rdx/confirmation';

function Confirmation() {
  const show = useSelector(getHasActiveConfirmation);
  const {
    id,
    resolve,
    reject,
    props: { title, body } = {},
  } = useSelector(getActiveConfirmation) || {};

  const dispatch = useDispatch();
  const close = () => dispatch(closeConfirmationByID(id));
  const onConfirm = () => {
    close();
    resolve();
  };
  const onCancel = () => {
    if (reject) {
      close();
      reject();
    }
    return false;
  };

  const defaultTitle = 'Title';
  const defaultBody = 'Body';

  return (
    <Modal
      show={show}
      container={document.getElementById('modals')}
      onHide={() => {}}
    >
      <Modal.Header className="font-weight-bold">
        {title || defaultTitle}
      </Modal.Header>
      <Modal.Body className="d-flex flex-fill flex-column">
        <div>
          {
            /* if string */ body && body.slice ? (
              <span style={{ whiteSpace: 'pre-wrap', textAlign: 'center' }}>
                {body || defaultBody}
              </span>
            ) : (
              body || defaultBody
            )
          }
        </div>
        <div className="d-flex justify-content-between mt-3">
          <Button
            variant="danger"
            className="font-weight-bold"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            className="font-weight-bold"
            onClick={onConfirm}
          >
            OK
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default Confirmation;

/**
 * @function ConfirmationFunction
 * Display a confirmation and resolve or reject when the user closes it
 * @param options
 * @param options.title The title of the confirmation dialog
 * @param options.body The body text of the confirmation dialog
 * @param options.noReject True if you don't want the user to have a 'cancel' option - guarantees that the promise will not reject
 * @return {Promise<void, void>}
 * @example
 * confirm({
 *     title: 'Are you sure you want to Foo the Bar',
 *     body: 'This action is permanent'
 * })
 *   .then(() => Foo(bar))
 *   .catch(() => {}))
 */

/**
 * Returns a function that can be called to display a confirmation popup
 * @returns {ConfirmationFunction}
 * @example
 * const MyComponent = () => {
 *   // Get the confirmation function
 *   const confirm = useConfirmation();
 *   // Use it as a promise to guard an action
 *   missileAlert = () => confirm({title: 'You better be damn sure'})
 *       .then(() => sendAlert())
 *       .catch(scoldUser)
 *   missileAlertTest = () => confirm({title: 'send TEST missile alert?'})
 *       .then(() => sendAlert({test: true})
 *       .catch(() => {}) // do nothing if cancelled
 *   // Clicking either button displays a confirmation first
 *   // and only does the action once the user accepts
 *   return (
 *     <div>
 *       <button onClick={missileAlert}>Send missile alert</button>
 *       <button onClick={missileAlertTest}>Send TEST missile alert</button>
 *     </div>
 *   )
 * }
 * @example
 * const MyComponent = () => {
 *   // The confirmation can also be informative, offering no way to cancel
 *   const confirm = useConfirmation();
 *   const missileAlert = () => sendAlert().then(confirm({noReject: true, body: 'The missile alert has been sent - you will be logged out'}))
 *   return <button onClick={missileAlert}>Send missile alert</button>
 * }
 */
export const useConfirmation = () => {
  const dispatch = useDispatch();

  return useCallback(
    ({ title, body, noReject = false }) =>
      new Promise((resolve, rej) => {
        const reject = noReject ? null : rej;
        dispatch(createConfirmation(resolve, reject, { title, body }));
      }),
    [dispatch],
  );
};
